import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  Waves,
  UtensilsCrossed,
  Wifi,
  Wind,
  Tv,
  ShieldCheck,
  Heart,
  MapPin,
  Users,
  Star,
  Phone,
} from "lucide-react";
import { HeroSlider } from "@/components/HeroSlider";
import { listRoomsAndBlocks } from "@/lib/booking.functions";
import { computeEffectivePrice, type Season } from "@/lib/pricing";
import { canonical } from "@/lib/seo";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Crystal Beach Bed and Breakfast - Malak BnB Resort" },
      { name: "description", content: "Stay at Malak BnB Resort in Crystal Beach, Ontario. Boutique rooms, breakfast, outdoor pool, and direct reservation requests near Lake Erie and Niagara Falls." },
      { property: "og:title", content: "Crystal Beach Bed and Breakfast - Malak BnB Resort" },
      { property: "og:description", content: "Boutique rooms, breakfast, outdoor pool, and direct reservation requests near Lake Erie and Niagara Falls." },
      { property: "og:url", content: "https://malakbnbresort.ca/" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
    ],
    links: [canonical("/")],
  }),
  component: HomePage,
});

const amenities = [
  { icon: Waves, label: "Outdoor Heated Pool" },
  { icon: UtensilsCrossed, label: "Continental Breakfast" },
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Wind, label: "Air Conditioning" },
  { icon: Tv, label: "Flat Screen TV" },
  { icon: ShieldCheck, label: "In-Room Safe" },
];

const ROOMS = [
  {
    name: "2 Double Beds",
    price: "C$345",
    image: "/images/room-double-beds.webp",
    alt: "Room with two double beds and hardwood floors",
    size: "450 sq ft",
    href: "/2-double-beds-room-crystal-beach",
  },
  {
    name: "Standard Double Bed",
    price: "C$345",
    image: "/images/room-standard-double.webp",
    alt: "Standard queen bed room with warm lighting",
    size: "400 sq ft",
    href: "/rooms",
  },
  {
    name: "Deluxe Queen Bed",
    price: "C$355",
    image: "/images/room-deluxe-queen.webp",
    alt: "Deluxe queen bed with elegant bathroom",
    size: "400 sq ft",
    href: "/deluxe-queen-room-crystal-beach",
  },
  {
    name: "Queen Bed Suite",
    price: "C$380",
    image: "/images/room-queen-suite.webp",
    alt: "Elegant suite with living area and chandelier",
    size: "440 sq ft",
    href: "/queen-bed-suite-crystal-beach",
  },
];

const GALLERY_IMAGES = [
  { src: "/images/gallery-staircase-hallway.webp", alt: "Cozy bedroom with queen bed and warm lighting", desc: "Warmly lit queen suite with hardwood floors and elegant drapery." },
  { src: "/images/gallery-pool-night.webp", alt: "Elegant bedroom with queen bed and warm lighting", desc: "Serene queen bed room with ambient lighting and a flat-screen TV." },
  { src: "/images/gallery-bathroom-vanity.webp", alt: "Bathroom with tub and toilet", desc: "Cozy bathroom with bathtub and soft pink walls." },
  { src: "/images/gallery-lounge.webp", alt: "Clean bathroom with bathtub and window", desc: "Modern bathroom with sliding glass door and natural light." },
  { src: "/images/gallery-bedroom.webp", alt: "Elegant bedroom with queen bed and desk", desc: "Spacious queen suite with hardwood floors and flat-screen TV." },
  { src: "/images/gallery-fitness.webp", alt: "Elegant bathroom vanity with mirror and lighting", desc: "Well-lit vanity with wooden accents, perfectly curated for comfort." },
  { src: "/images/gallery-bathroom-mirror.webp", alt: "Dining area with buffet setup", desc: "Start your day in our elegant dining area with chandelier above." },
  { src: "/images/gallery-staircase-chandelier.webp", alt: "Outdoor heated pool with lounge chairs", desc: "Dive into relaxation at our outdoor poolside oasis." },
  { src: "/images/gallery-bedroom-warm.webp", alt: "Room with two double beds and hardwood floor", desc: "Serene comfort just steps from Lake Erie." },
  { src: "/images/gallery-dining.webp", alt: "Exterior view of Malak BnB Resort entrance", desc: "The welcoming facade of Malak BnB Resort steps from Bay Beach." },
  { src: "/images/gallery-pool-outdoor.webp", alt: "Elegant bathroom with granite vanity", desc: "Thoughtfully designed bathrooms with granite and wooden accents." },
  { src: "/images/gallery-private-bath.webp", alt: "Room with two double beds and desk", desc: "Comfortable double room with warm lighting and elegant décor." },
  { src: "/images/gallery-shower.webp", alt: "Elegant hallway with yellow walls", desc: "A warm embrace of elegance and color in our inviting hallway." },
  { src: "/images/gallery-bedroom-curtains.webp", alt: "Home gym with exercise equipment", desc: "Stay active in our intimate gym space with treadmill and bikes." },
  { src: "/images/events-setup.webp", alt: "Elegant dining table with ornate chairs", desc: "Experience intimate gatherings with exquisite dining setups." },
  { src: "/images/events-hallway.webp", alt: "Elegant chandelier with wrought iron railing", desc: "Grand chandelier above decorative wrought iron railings." },
];

function HomePage() {
  const listFn = useServerFn(listRoomsAndBlocks);
  const { data } = useQuery({ queryKey: ["rooms-and-blocks"], queryFn: () => listFn() });
  const today = new Date();
  const priceFor = (roomName: string, fallback: string): string => {
    const dbRoom = data?.rooms.find((r) => r.name === roomName);
    if (!dbRoom) return fallback;
    const effective = computeEffectivePrice(Number(dbRoom.price_cad), today, (data?.seasons ?? []) as Season[]);
    return `C$${effective}`;
  };
  return (
    <div>
      {/* 1. ARRIVE - Hero Slider */}
      <HeroSlider />

      {/* 5-star stay ribbon */}
      <div className="bg-primary/10 border-y border-primary/10 py-3">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-sm">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <span className="font-semibold text-foreground">5-Star Stay</span>
          <span className="hidden sm:inline text-muted-foreground">|</span>
          <span className="text-muted-foreground">Heated pool, breakfast buffet, A/C, Wi-Fi, in-room safe, and flat-screen TV</span>
        </div>
      </div>

      {/* 2. ARRIVE - Welcome narrative */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="animate-fade-in-up">
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-4">Welcome to Malak</p>
              <p className="prose-quote mb-6">
                "From the moment you arrive,<br />you're home."
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Malak BnB Resort is a boutique, family-owned retreat on the shores of Crystal Beach,
                Ontario. Situated steps from Bay Beach and Lake Erie, we've designed every corner
                to capture the calm, unhurried spirit of a lakeside escape, with the warmth only
                a family-run home can offer.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Enjoy our outdoor heated pool, continental breakfast buffet, and indoor and outdoor
                lounge areas. Niagara Falls is just 30 minutes away.
              </p>
              <div className="mt-8 flex flex-wrap gap-8">
                {[
                  { value: "4", label: "Room types" },
                  { value: "5 min", label: "Walk to Bay Beach" },
                  { value: "30 min", label: "To Niagara Falls" },
                ].map((stat) => (
                  <div key={stat.label} className="stat-item text-center">
                    <div className="text-2xl font-heading text-primary">{stat.value}</div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Link
                to="/explore"
                className="mt-8 inline-flex items-center text-primary font-semibold text-sm hover:underline"
              >
                Discover Crystal Beach →
              </Link>
            </div>
            <div className="animate-fade-in-up animation-delay-200">
              <img
                src="/images/property-front-signboard.webp"
                alt="Stone exterior of Malak BnB Resort in Crystal Beach with the branded signboard in the front garden"
                width={1050}
                height={1400}
                loading="lazy"
                decoding="async"
                className="w-full rounded-2xl shadow-xl object-cover aspect-[4/5]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. SETTLE IN - All 4 rooms */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Your Room</p>
            <h2 className="text-3xl sm:text-4xl font-heading text-foreground">
              Settle Into Your Sanctuary
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Four beautifully appointed rooms, each with plush bedding, hardwood floors, and
              everything you need to truly unwind.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ROOMS.map((room) => (
              <Link
                key={room.name}
                to={room.href as any}
                className="group block rounded-2xl overflow-hidden bg-background shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={room.image}
                    alt={room.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold text-foreground shadow">
                    From {priceFor(room.name, room.price)}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg text-foreground">{room.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{room.size}</p>
                  <p className="text-sm text-primary font-medium mt-2">View details →</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/rooms"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              View All Rooms & Rates
            </Link>
          </div>
        </div>
      </section>

      {/* 4. BREATHE - Pool interlude */}
      <section className="interlude-section relative h-[420px] sm:h-[500px]">
        <img
          src="/images/gallery-staircase-chandelier.webp"
          alt="Outdoor heated pool at Malak BnB Resort with lounge chairs"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
          <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-4">Everything Included</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-white max-w-2xl leading-tight">
            Warm waters, fresh mornings, and nothing left to want.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-10 sm:gap-16">
            {amenities.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                  <a.icon className="h-5 w-5 text-white" />
                </div>
                <span className="text-xs font-medium text-white/80 text-center max-w-[80px] leading-tight">{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. EXPLORE - Crystal Beach location */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/explore-crystal-beach.webp"
                alt="Cyclist on a sunny street in Crystal Beach town"
                className="w-full object-cover aspect-[4/3]"
              />
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">The Neighbourhood</p>
              <h2 className="text-3xl sm:text-4xl font-heading text-foreground leading-snug">
                Step Outside & Explore Crystal Beach
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Stroll to golden sandy shores, rent a bike through charming town streets, cast a
                line at the marina, or take a short drive to Niagara Falls. Crystal Beach is a
                community that moves at the perfect pace, and it's all on your doorstep.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                {[
                  { icon: MapPin, text: "264 Ridge Road South, Crystal Beach, Ontario" },
                  { icon: Waves, text: "5-minute walk to Bay Beach" },
                  { icon: MapPin, text: "30 minutes to Niagara Falls" },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Icon className="h-4 w-4 text-primary shrink-0" />
                    {text}
                  </div>
                ))}
              </div>
              <Link
                to="/explore"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Explore the Area
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 6. GATHER - Events teaser */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Gatherings & Events</p>
              <h2 className="text-3xl sm:text-4xl font-heading text-foreground leading-snug">
                Make it a Moment Worth Remembering
              </h2>
              <p className="mt-6 text-muted-foreground leading-relaxed">
                Our elegant venue hosts intimate gatherings of up to 25 guests. Weddings by the
                pool, birthday dinners, graduations, corporate retreats: our team brings your
                vision to life with personalized planning and in-house catering featuring
                kosher, Middle Eastern, and Italian-inspired cuisine.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-4">
                {[
                  { value: "25", label: "Max guests" },
                  { value: "3", label: "Cuisine styles" },
                  { value: "1", label: "Dedicated team" },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background rounded-xl p-4 text-center border border-border/50">
                    <div className="text-2xl font-heading text-primary">{stat.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Link
                to="/meetings-and-events"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Plan Your Event
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/events-setup.webp"
                alt="Elegant dining table with ornate chairs and fine tableware"
                className="w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-16 bg-primary/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { icon: Heart, title: "Family-Owned", desc: "Warm, personal hospitality from our family to yours" },
              {
                icon: Star,
                title: "5-Star Stay",
                desc: "Amenities and service that meet 5-star stay standards, including pool, breakfast, and A/C",
                stars: true,
              },
              { icon: Users, title: "Personalized Service", desc: "We confirm every booking personally to ensure your comfort" },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center gap-3">
                {item.stars ? (
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-7 w-7 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                ) : (
                  <item.icon className="h-8 w-8 text-primary" />
                )}
                <h3 className="font-heading text-lg text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground max-w-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guest Reviews */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Guest Reviews</p>
            <h2 className="text-3xl sm:text-4xl font-heading text-foreground">What Our Guests Say</h2>
            <p className="mt-3 text-muted-foreground text-sm">
              Praised for Service &middot; Cleanliness &middot; Breakfast
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* TripAdvisor review */}
            <div className="bg-background rounded-2xl p-7 shadow-sm border border-border/50 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 bg-[#34E0A1]/10 text-[#007A50] text-xs font-bold px-3 py-1 rounded-full">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                  TripAdvisor
                </span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
              <div>
                <p className="font-heading text-lg text-foreground">"A hidden gem"</p>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  "This bed and breakfast is lovely. Jay and Hiba welcome you with open arms into their home.
                  The surroundings are lovely and Gina's flair for decorating is apparent everywhere.
                  For an additional fee Hiba will cook dinner for you. She's a wonderful cook and hostess.
                  I ended up eating in every night rather than going to a local restaurant. Highly recommended."
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-border/50">
                <p className="text-sm font-semibold text-foreground">honint123</p>
                <p className="text-xs text-muted-foreground">Peterborough, Canada &middot; Travelled on business &middot; Oct 2018</p>
              </div>
            </div>

            {/* Google review 1 */}
            <div className="bg-background rounded-2xl p-7 shadow-sm border border-border/50 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1 rounded-full">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
              <div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  "A beautiful place with delicious food and great service. Everything exceeded our expectations.
                  The hospitality made us feel completely at home from the moment we arrived."
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-border/50">
                <p className="text-sm font-semibold text-foreground">Ghada EL-OUD</p>
                <p className="text-xs text-muted-foreground">Google Review</p>
              </div>
            </div>

            {/* Google review 2 */}
            <div className="bg-background rounded-2xl p-7 shadow-sm border border-border/50 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1 rounded-full">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
              <div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  "Malak B&B offers dinner service as well and it does not disappoint. The food was outstanding
                  and the atmosphere was warm and welcoming. Truly a special place to stay."
                </p>
              </div>
              <div className="mt-auto pt-4 border-t border-border/50">
                <p className="text-sm font-semibold text-foreground">Felix Sarduy</p>
                <p className="text-xs text-muted-foreground">Google Review</p>
              </div>
            </div>
          </div>

          {/* Leave a review + platform links */}
          <div className="mt-12 flex flex-col items-center gap-8">
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJu6jjv8U904kRtKWZ5TBoPCw"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
            >
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              Leave a Review on Google
            </a>
            <div className="flex flex-wrap justify-center gap-6">
              <a
                href="https://www.tripadvisor.ca/Hotel_Review-g154987-d14426463-Reviews-Malak_Bnb-Crystal_Beach_Fort_Erie_Ontario.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#007A50] hover:underline"
              >
                Read more on TripAdvisor →
              </a>
              <a
                href="https://www.google.com/travel/search?q=Malak%20BnB%20Resort&ap=ugEHcmV2aWV3cw"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:underline"
              >
                Read more on Google →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 7. REMEMBER - Gallery */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">The Resort</p>
            <h2 className="text-3xl sm:text-4xl font-heading text-foreground">A Glimpse of What Awaits</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Every room, every view, every detail, crafted to make you feel at ease from the moment you arrive.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {GALLERY_IMAGES.map((img, i) => (
              <div key={i} className="gallery-item rounded-2xl overflow-hidden aspect-[4/3]">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="gallery-overlay">
                  <p className="text-white text-xs leading-relaxed">{img.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. BOOK - Final CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-primary-foreground leading-tight">
            Your lakeside escape is<br />one request away.
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and we'll personally confirm your stay and share
            payment details with you directly. No third-party fees, just a direct connection.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request to Book
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
